import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingCsvRowStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { BiddingIntradayCsvRowStatus } from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'

import { KOMA_COUNT } from '~/constants/koma'
import { type CivilDate, civilDateToInputValue } from '~/models/civilDate'
import { sanitizeCsvImportReturnTo } from '~/models/csvImportReturn'
import { parseIsoDate } from '~/models/dateTime'

/**
 * 入札 CSV (スポット通常・スポットブロック・時間前) を確定したあと、 入札画面へ移って
 * 取込の結果 (成功のトースト、 時間前で除いた行があればエラーバナー) を出すための純関数と型。
 *
 * - スポット: spot-bid-normal-csv-import.md AC-SPOT-024 (ブロックは spot-bid-block-csv-import.md §7 が従う)
 * - 時間前: intraday-csv-import.md AC-BCI-038 / AC-BCI-039
 *
 * 共通の `csvImportApplied` (`csvImportReturn.ts`。 作成・更新・削除の 3 件数) とは件数の形が違うので、
 * 別のキー `bidCsvImportApplied` で location state に載せる。
 */

/** 取り込んだ CSV の種別。 入札画面は自分の種別の結果だけを受け取る */
export type BidCsvImportKind = 'spotNormal' | 'spotBlock' | 'intraday'

/** 取込結果の受渡日ごとの件数。 date は YYYY-MM-DD */
export type BidCsvImportDateCount = { date: string; count: number }

/** 取込結果の発注主体区分ごとの件数 */
export type BidCsvImportIssuerCount = { issuerType: LicenseType; count: number }

/** 確定で対象外にした件数 (時間前だけ。 スポットは常に 0) */
export type BidCsvImportExcluded = {
  gcClosed: number
  issuerUnregistered: number
}

export type BidCsvImportNotice = {
  kind: BidCsvImportKind
  /** apply 応答の件数 (対象外の行は含まない) */
  importedCount: number
  /** 受渡日の昇順 */
  byDate: BidCsvImportDateCount[]
  /** CSV に出た順 */
  byIssuer: BidCsvImportIssuerCount[]
  /** 取り込んだ札のエリア (重複なし) */
  areas: AreaCode[]
  /** 取り込んだ札の売買 (重複なし) */
  sides: Array<'buy' | 'sell'>
  /**
   * 取り込んだ札のコマ番号 (重複なし、 CSV に出た順)。 時間前だけが持ち、 入札一覧のコマの絞り込みを
   * 広げるのに使う (AC-BCI-038)。 スポットは持たない
   */
  komas?: number[]
  excluded: BidCsvImportExcluded
}

/** 入札画面へ移る時の location state の形 */
export type BidCsvImportReturnState = {
  bidCsvImportApplied: BidCsvImportNotice
}

/**
 * 集計に使う、 check 応答の行 1 件ぶんの共通部分。
 * スポット通常・ブロック・時間前の行は別の proto message だが、 集計に要る項目は同じ形。
 */
type CheckedRowBase = {
  targetDate?: CivilDate
  areaCode: AreaCode
  direction: Side
  issuerType: LicenseType
}

/** 札 (または 1 組) 1 件ぶん。 件数の単位 */
type CountedBid = {
  date: string
  issuerType: LicenseType
  areas: AreaCode[]
  sides: Side[]
}

const EMPTY_EXCLUDED: BidCsvImportExcluded = {
  gcClosed: 0,
  issuerUnregistered: 0,
}

function toSide(side: Side): 'buy' | 'sell' | undefined {
  if (side === Side.BUY) return 'buy'
  if (side === Side.SELL) return 'sell'
  return undefined
}

function pushUnique<T>(list: T[], value: T): void {
  if (!list.includes(value)) list.push(value)
}

/** 件数の単位ごとの情報から、 受渡日・発注主体区分・エリア・売買の集計を作る */
function summarize(
  bids: readonly CountedBid[],
): Pick<BidCsvImportNotice, 'byDate' | 'byIssuer' | 'areas' | 'sides'> {
  const dateCounts = new Map<string, number>()
  const byIssuer: BidCsvImportIssuerCount[] = []
  const areas: AreaCode[] = []
  const sides: Array<'buy' | 'sell'> = []
  for (const bid of bids) {
    dateCounts.set(bid.date, (dateCounts.get(bid.date) ?? 0) + 1)
    const issuer = byIssuer.find((i) => i.issuerType === bid.issuerType)
    if (issuer === undefined) {
      byIssuer.push({ issuerType: bid.issuerType, count: 1 })
    } else {
      issuer.count += 1
    }
    for (const area of bid.areas) pushUnique(areas, area)
    for (const side of bid.sides) {
      const mapped = toSide(side)
      if (mapped !== undefined) pushUnique(sides, mapped)
    }
  }
  // YYYY-MM-DD はゼロ埋めなので文字列の昇順が日付の昇順になる
  const byDate = [...dateCounts.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, count]) => ({ date, count }))
  return { byDate, byIssuer, areas, sides }
}

function toCountedBid(row: CheckedRowBase): CountedBid | undefined {
  const date = civilDateToInputValue(row.targetDate)
  if (date === '') return undefined
  return {
    date,
    issuerType: row.issuerType,
    areas: [row.areaCode],
    sides: [row.direction],
  }
}

/**
 * スポット通常入札の取込結果を作る。 check 応答の CREATED の行を受渡日・発注主体区分・
 * エリア・売買で数える (AC-SPOT-024)。 件数は apply 応答の件数。
 * スポットは確定時に行を対象外として除かない (違反は取込全体の拒否) ので、 excluded は常に 0。
 */
export function buildSpotNormalNotice(
  rows: ReadonlyArray<CheckedRowBase & { status: BiddingCsvRowStatus }>,
  appliedCount: number,
): BidCsvImportNotice {
  const bids = rows
    .filter((r) => r.status === BiddingCsvRowStatus.CREATED)
    .flatMap((r) => toCountedBid(r) ?? [])
  return {
    kind: 'spotNormal',
    importedCount: appliedCount,
    ...summarize(bids),
    excluded: EMPTY_EXCLUDED,
  }
}

/**
 * スポットブロック入札の取込結果を作る。 CREATED の行を `pairNo` でまとめて数える
 * (LINK / LOOP の 1 組を 1 件。 pairNo が空 = 通常ブロックは 1 行を 1 件)。
 * 組の受渡日・発注主体区分は最初の行の値、 エリア・売買は組の全行の値を取る。
 * 件数は apply 応答の作成グループ数。
 */
export function buildSpotBlockNotice(
  rows: ReadonlyArray<
    CheckedRowBase & { status: BiddingCsvRowStatus; pairNo: string }
  >,
  appliedGroupCount: number,
): BidCsvImportNotice {
  const bids: CountedBid[] = []
  const pairs = new Map<string, CountedBid>()
  for (const row of rows) {
    if (row.status !== BiddingCsvRowStatus.CREATED) continue
    const counted = toCountedBid(row)
    if (counted === undefined) continue
    if (row.pairNo === '') {
      bids.push(counted)
      continue
    }
    const pair = pairs.get(row.pairNo)
    if (pair === undefined) {
      pairs.set(row.pairNo, counted)
      bids.push(counted)
    } else {
      for (const area of counted.areas) pushUnique(pair.areas, area)
      for (const side of counted.sides) pushUnique(pair.sides, side)
    }
  }
  return {
    kind: 'spotBlock',
    importedCount: appliedGroupCount,
    ...summarize(bids),
    excluded: EMPTY_EXCLUDED,
  }
}

/**
 * 時間前入札の取込結果を作る (AC-BCI-038 / AC-BCI-039)。
 *
 * apply 応答が返した確定時点の実結果を使う。 CREATED の行だけから受渡日・発注主体区分・
 * エリア・売買を数え、 対象外は理由ごとの status から数える。
 */
export function buildIntradayNotice(
  rows: ReadonlyArray<
    CheckedRowBase & { status: BiddingIntradayCsvRowStatus; timeCode: number }
  >,
  appliedCount: number,
): BidCsvImportNotice {
  let gcClosed = 0
  let issuerUnregistered = 0
  const bids: CountedBid[] = []
  const komas: number[] = []
  for (const row of rows) {
    switch (row.status) {
      case BiddingIntradayCsvRowStatus.CREATED: {
        const counted = toCountedBid(row)
        if (counted !== undefined) {
          bids.push(counted)
          pushUnique(komas, row.timeCode)
        }
        break
      }
      case BiddingIntradayCsvRowStatus.EXCLUDED_GC_CLOSED:
        gcClosed += 1
        break
      case BiddingIntradayCsvRowStatus.EXCLUDED_ISSUER_AREA_UNREGISTERED:
        issuerUnregistered += 1
        break
      default:
        break
    }
  }
  return {
    kind: 'intraday',
    importedCount: appliedCount,
    ...summarize(bids),
    komas,
    excluded: { gcClosed, issuerUnregistered },
  }
}

/** 確定時に対象外として除いた行があるか (AC-BCI-038。 あれば成功のトーストに代えてエラーバナーで知らせる) */
export function hasBidCsvImportExcluded(notice: BidCsvImportNotice): boolean {
  return notice.excluded.gcClosed > 0 || notice.excluded.issuerUnregistered > 0
}

/** 入札画面へ移る時の location state を作る */
export function toBidCsvImportReturnState(
  notice: BidCsvImportNotice,
): BidCsvImportReturnState {
  return { bidCsvImportApplied: notice }
}

/**
 * 取込画面の returnTo (検証済み) が、その種別の入札画面を指す時はそれを使う
 * (`?workflowId=` など今の画面の query を保つため)。 それ以外は入札画面の path。
 * bidPagePath は query を持てる (`?workflowId=` 付きの入札画面)。
 */
export function resolveBidCsvImportDestination(
  rawReturnTo: string | null,
  currentPathname: string,
  bidPagePath: string,
): string {
  const returnTo = sanitizeCsvImportReturnTo(rawReturnTo, currentPathname)
  if (returnTo === undefined) return bidPagePath
  const pathname = returnTo.split(/[?#]/)[0]
  return pathname === bidPagePath.split(/[?#]/)[0] ? returnTo : bidPagePath
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isKnownEnum(en: object, value: unknown): value is number {
  return (
    typeof value === 'number' &&
    (en as Record<number, unknown>)[value] !== undefined
  )
}

function readKoma(value: unknown): number | undefined {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= KOMA_COUNT
    ? value
    : undefined
}

function readArray<T>(
  value: unknown,
  readItem: (item: unknown) => T | undefined,
): T[] | undefined {
  if (!Array.isArray(value)) return undefined
  const items: T[] = []
  for (const raw of value as unknown[]) {
    const item = readItem(raw)
    if (item === undefined) return undefined
    items.push(item)
  }
  return items
}

/**
 * location state から、 指定した種別の入札 CSV の取込結果だけを読む。 形が違うもの・別の種別のものは
 * 無視する (location state は履歴に残るので、 型を信用せずに確かめる)。
 */
export function readBidCsvImportAppliedNotice(
  state: unknown,
  kinds: readonly BidCsvImportKind[],
): BidCsvImportNotice | undefined {
  if (!isRecord(state)) return undefined
  const notice = state.bidCsvImportApplied
  if (!isRecord(notice)) return undefined
  const {
    kind,
    importedCount,
    byDate,
    byIssuer,
    areas,
    sides,
    komas,
    excluded,
  } = notice
  if (!kinds.includes(kind as BidCsvImportKind)) return undefined
  if (!isCount(importedCount) || !isRecord(excluded)) return undefined
  if (!isCount(excluded.gcClosed) || !isCount(excluded.issuerUnregistered)) {
    return undefined
  }
  const readByDate = readArray(
    byDate,
    (item): BidCsvImportDateCount | undefined => {
      if (!isRecord(item)) return undefined
      if (typeof item.date !== 'string' || !isCount(item.count))
        return undefined
      if (parseIsoDate(item.date) === undefined) return undefined
      return { date: item.date, count: item.count }
    },
  )
  const readByIssuer = readArray(
    byIssuer,
    (item): BidCsvImportIssuerCount | undefined => {
      if (!isRecord(item)) return undefined
      if (!isKnownEnum(LicenseType, item.issuerType)) return undefined
      if (!isCount(item.count)) return undefined
      return { issuerType: item.issuerType as LicenseType, count: item.count }
    },
  )
  const readAreas = readArray(areas, (item) =>
    isKnownEnum(AreaCode, item) ? (item as AreaCode) : undefined,
  )
  const readSides = readArray(sides, (item) =>
    item === 'buy' || item === 'sell' ? item : undefined,
  )
  const readKomas = komas === undefined ? undefined : readArray(komas, readKoma)
  if (
    readByDate === undefined ||
    readByIssuer === undefined ||
    readAreas === undefined ||
    readSides === undefined ||
    (komas !== undefined && readKomas === undefined)
  ) {
    return undefined
  }
  return {
    kind: kind as BidCsvImportKind,
    importedCount,
    byDate: readByDate,
    byIssuer: readByIssuer,
    areas: readAreas,
    sides: readSides,
    ...(readKomas === undefined ? {} : { komas: readKomas }),
    excluded: {
      gcClosed: excluded.gcClosed,
      issuerUnregistered: excluded.issuerUnregistered,
    },
  }
}

/**
 * 取込結果の受渡日のうち最も早い日 (着地で選ぶ日)。 byDate は昇順なので先頭。 無ければ undefined。
 * ワークフローで受渡日が固定されている時に変えない判断は呼び出し側が持つ。
 */
export function earliestBidCsvImportDate(
  notice: BidCsvImportNotice,
): Date | undefined {
  const first = notice.byDate[0]
  return first === undefined ? undefined : parseIsoDate(first.date)
}
